import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { DriverRulesProvider } from "@/contexts/DriverRulesContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PERMISSIONS, ROUTE_PERMISSIONS } from "@/lib/permissions";
import { LoginPage } from "@/pages/LoginPage";
import { Dashboard } from "@/pages/Dashboard";
import { ChargersDashboard } from "@/pages/ChargersDashboard";
import { TagsPage } from "@/pages/TagsPage";
import { TagDetailPage } from "@/pages/TagDetailPage";
import { TicketsPage } from "@/pages/TicketsPage";
import { CategoriesManagement } from "@/pages/CategoriesManagement";
import { OccurrencePrioritiesPage } from "@/pages/OccurrencePrioritiesPage";
import { ExternalContactTypesPage } from "@/pages/ExternalContactTypesPage";
import { ResourceStatusesPage } from "@/pages/ResourceStatusesPage";
import { TagDeactivationReasonsPage } from "@/pages/TagDeactivationReasonsPage";
import { MassAlertTemplatesPage } from "@/pages/MassAlertTemplatesPage";
import { IncidentsPage } from "@/pages/IncidentsPage";
import { UsersManagement } from "@/pages/UsersManagement";
import { PermissionsPage } from "@/pages/PermissionsPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { UserReportPage } from "@/pages/UserReportPage";
import { IncidentTypesConfig } from "@/pages/IncidentTypesConfig";
import { MassAlerts } from "@/pages/MassAlerts";
import { FleetDashboard } from "@/pages/FleetDashboard";
import { ActivityLog } from "@/pages/ActivityLog";
import { ReportsPage } from "@/pages/ReportsPage";
import { ContactRulesPage } from "@/pages/ContactRulesPage";
import { FleetMapPage } from "@/pages/FleetMapPage";
import { DriverContactRulesPage } from "@/pages/DriverContactRulesPage";
import { AccountPage } from "@/pages/AccountPage";
import { FirstLoginPage } from "@/pages/FirstLoginPage";
import { PasswordResetRequestPage } from "@/pages/PasswordResetRequestPage";
import { ExternalContactsPage } from "@/pages/ExternalContactsPage";
import { ExternalContactsProvider } from "@/contexts/ExternalContactsContext";

const Protected = ({ children, path, permissions }) => (
  <ProtectedRoute permissions={permissions ?? ROUTE_PERMISSIONS[path]}>{children}</ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
      <BrandingProvider>
      <ExternalContactsProvider>
      <DriverRulesProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/password/reset" element={<PasswordResetRequestPage />} />
          <Route path="/password/reset/:token" element={<PasswordResetRequestPage />} />
          <Route path="/reset-password" element={<PasswordResetRequestPage />} />
          <Route path="/reset-password/:token" element={<PasswordResetRequestPage />} />
          <Route path="/first-login" element={<Protected path="/first-login"><FirstLoginPage /></Protected>} />
          <Route path="/email/verify" element={<FirstLoginPage />} />
          <Route path="/email/verify/:id/:hash" element={<FirstLoginPage />} />
          <Route path="/report/:tagId" element={<UserReportPage />} />

          {/* Protected */}
          <Route path="/" element={<Protected permissions={[PERMISSIONS.DASHBOARD_READ]} path="/"><Dashboard /></Protected>} />
          <Route path="/chargers" element={<Protected permissions={[PERMISSIONS.RESOURCE_READ]} path="/chargers"><ChargersDashboard /></Protected>} />
          <Route path="/tags" element={<Protected permissions={[PERMISSIONS.TAG_READ]} path="/tags"><TagsPage /></Protected>} />
          <Route path="/tags/:tagId" element={<Protected permissions={[PERMISSIONS.TAG_READ]} path="/tags"><TagDetailPage /></Protected>} />
          <Route path="/tickets" element={<Protected permissions={[PERMISSIONS.TICKET_READ]} path="/tickets"><TicketsPage /></Protected>} />
          <Route path="/categories" element={<Protected permissions={[PERMISSIONS.DEPARTMENT_READ]} path="/categories"><CategoriesManagement /></Protected>} />
          <Route path="/occurrence-priorities" element={<Protected permissions={[PERMISSIONS.SETTINGS_READ]} path="/occurrence-priorities"><OccurrencePrioritiesPage /></Protected>} />
          <Route path="/external-contact-types" element={<Protected permissions={[PERMISSIONS.SETTINGS_READ]} path="/external-contact-types"><ExternalContactTypesPage /></Protected>} />
          <Route path="/resource-statuses" element={<Protected permissions={[PERMISSIONS.SETTINGS_READ]} path="/resource-statuses"><ResourceStatusesPage /></Protected>} />
          <Route path="/tag-deactivation-reasons" element={<Protected permissions={[PERMISSIONS.SETTINGS_READ]} path="/tag-deactivation-reasons"><TagDeactivationReasonsPage /></Protected>} />
          <Route path="/mass-alert-templates" element={<Protected permissions={[PERMISSIONS.MASS_ALERT_READ]} path="/mass-alert-templates"><MassAlertTemplatesPage /></Protected>} />
          <Route path="/incidents" element={<Protected permissions={[PERMISSIONS.INCIDENT_READ]} path="/incidents"><IncidentsPage /></Protected>} />
          <Route path="/users" element={<Protected permissions={[PERMISSIONS.USER_READ]} path="/users"><UsersManagement /></Protected>} />
          <Route path="/permissions" element={<Protected permissions={[PERMISSIONS.PERMISSION_READ]} path="/permissions"><PermissionsPage /></Protected>} />
          <Route path="/settings" element={<Protected permissions={[PERMISSIONS.SETTINGS_READ]} path="/settings"><SettingsPage /></Protected>} />
          <Route path="/incident-types" element={<Protected permissions={[PERMISSIONS.INCIDENT_TYPE_READ]} path="/incident-types"><IncidentTypesConfig /></Protected>} />
          <Route path="/mass-alerts" element={<Protected permissions={[PERMISSIONS.MASS_ALERT_READ]} path="/mass-alerts"><MassAlerts /></Protected>} />
          <Route path="/fleet" element={<Protected permissions={[PERMISSIONS.RESOURCE_READ]}><FleetDashboard /></Protected>} />
          <Route path="/activity-log" element={<Protected permissions={[PERMISSIONS.ACTIVITY_LOG_READ]} path="/activity-log"><ActivityLog /></Protected>} />
          <Route path="/reports" element={<Protected permissions={[PERMISSIONS.REPORT_READ]} path="/reports"><ReportsPage /></Protected>} />
          <Route path="/contact-rules" element={<Protected permissions={[PERMISSIONS.CONTACT_RULE_GROUP_READ, PERMISSIONS.CONTACT_RULE_READ]} path="/contact-rules"><ContactRulesPage /></Protected>} />
          <Route path="/driver-contact-rules" element={<Protected permissions={[PERMISSIONS.DRIVER_RULE_READ]} path="/driver-contact-rules"><DriverContactRulesPage /></Protected>} />
          <Route path="/map" element={<Protected permissions={[PERMISSIONS.RESOURCE_READ]} path="/map"><FleetMapPage /></Protected>} />
          <Route path="/account" element={<Protected path="/account"><AccountPage /></Protected>} />
          <Route path="/external-contacts" element={<Protected permissions={[PERMISSIONS.EXTERNAL_CONTACT_READ]} path="/external-contacts"><ExternalContactsPage /></Protected>} />
        </Routes>
      </BrowserRouter>
      </DriverRulesProvider>
      </ExternalContactsProvider>
      </BrandingProvider>
      </AuthProvider>
      <Toaster />
    </div>
  );
}

export default App;
